/**
 * Email Processing Service
 *
 * API service for email processing history and statistics.
 * Similar to module transactions and tampering detection.
 */

import api from '../shared/services/api';

export interface EmailThread {
  id: number;
  sender_email: string;
  sender_name: string | null;
  subject: string;
  message_id: string;
  status: string;
  is_declaration: boolean;
  declaration_state: string | null;
  received_at: string;
  processed_at: string | null;
  completed_at: string | null;
  processing_time: string | null;
  total_attachments: number;
  processed_attachments: number;
  failed_attachments: number;
  reply_count: number;
  last_reply_sent_at: string | null;
  error_message: string | null;
  attachments?: EmailAttachment[];
}

export interface EmailAttachment {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  document_type: string | null;
  document_type_confidence: number | null;
  status: string;
  error_message: string | null;
  processed_at: string | null;
}

export interface EmailThreadDetails extends EmailThread {
  body_text: string | null;
  body_html: string | null;
  conversation_data: any;
  conversation_messages: any[];
  mandatory_fields_status: any;
  atlas_submission_response: any;
  attachments: EmailAttachment[];
}

export interface EmailProcessingHistoryResponse {
  email_threads: EmailThread[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface EmailProcessingStats {
  total_threads: number;
  by_status: {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  };
  total_declarations: number;
  total_attachments: number;
}

export interface AdminEmailThread extends EmailThread {
  user_id: string;
  user_email: string;
  user_name: string;
}

export interface AdminEmailProcessingHistoryResponse {
  email_threads: AdminEmailThread[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

class EmailProcessingService {
  /**
   * Get email processing history for current user
   */
  async getHistory(
    page: number = 1,
    perPage: number = 10,
    status?: string,
    search?: string
  ): Promise<EmailProcessingHistoryResponse> {
    const params: any = { page, per_page: perPage };
    if (status) params.status = status;
    if (search) params.search = search;

    const response = await api.get('/api/v1/email-processing/history', { params });
    return response.data;
  }

  /**
   * Get detailed information about a specific email thread
   */
  async getThreadDetails(threadId: number): Promise<EmailThreadDetails> {
    const response = await api.get(`/api/v1/email-processing/history/${threadId}`);
    return response.data;
  }

  /**
   * Get email processing statistics for current user
   */
  async getStats(): Promise<EmailProcessingStats> {
    const response = await api.get('/api/v1/email-processing/stats');
    return response.data;
  }

  /**
   * Get all email processing history (admin only)
   */
  async getAdminHistory(
    page: number = 1,
    perPage: number = 10,
    userId?: string,
    status?: string,
    search?: string
  ): Promise<AdminEmailProcessingHistoryResponse> {
    const params: any = { page, per_page: perPage };
    if (userId) params.user_id = userId;
    if (status) params.status = status;
    if (search) params.search = search;

    const response = await api.get('/api/v1/email-processing/admin/history', { params });
    return response.data;
  }

  /**
   * Get detailed information about any email thread (admin only)
   */
  async getAdminThreadDetails(threadId: number): Promise<EmailThreadDetails & { user_id: string; user_email: string; user_name: string }> {
    const response = await api.get(`/api/v1/email-processing/admin/history/${threadId}`);
    return response.data;
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get status badge color
   */
  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  }

  /**
   * Get document type icon
   */
  getDocumentTypeIcon(documentType: string | null): string {
    if (!documentType) return '📄';

    const type = documentType.toLowerCase();
    if (type.includes('invoice')) return '🧾';
    if (type.includes('packing')) return '📦';
    if (type.includes('certificate')) return '📜';
    if (type.includes('declaration')) return '📋';
    if (type.includes('customs')) return '🛃';
    return '📄';
  }

  /**
   * Download an attachment file
   */
  async downloadAttachment(threadId: number, attachmentId: number): Promise<any> {
    const response = await api.get(
      `/api/v1/email-processing/attachment/${threadId}/${attachmentId}/download`,
      { responseType: 'blob' }
    );
    return response;
  }
}

export default new EmailProcessingService();
